import type { BeneficiaryCreationFailedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryCreationFailed';
import type { BeneficiaryUpdateFailedAction } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiaryUpdateFailed';
import CreateBeneficiary from '@Application/Beneficiaries/Beneficiaries/Commands/CreateBeneficiary';
import UpdateBeneficiary from '@Application/Beneficiaries/Beneficiaries/Commands/UpdateBeneficiary';
import FetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/Queries/FetchBeneficiary';
import createBeneficiary from '@Application/Beneficiaries/Beneficiaries/createBeneficiary';
import fetchBeneficiary from '@Application/Beneficiaries/Beneficiaries/fetchBeneficiary';
import updateBeneficiary from '@Application/Beneficiaries/Beneficiaries/updateBeneficiary';
import FormSection from '@Guidelines/Component/FormSection';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import PlainDate from '@Domain/PlainDate';
import useBeneficiaryAccess from '@UI/Hooks/useBeneficiaryAccess';
import AssignedUsersField from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar/AssignedUsersField';
import BeneficiaryFormData from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar/FormData/BeneficiaryFormData';
import useBeneficiaryNameHomonymCheck from '@UI/Pages/Beneficiaries/Sidebars/UpsertBeneficiarySidebar/Hooks/useBeneficiaryNameHomonymCheck';
import beneficiarySidebarSignal, {
	closeBeneficiarySidebar,
} from '@UI/Pages/Beneficiaries/Signals/beneficiarySidebarSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import { css } from '@emotion/react';
import * as React from 'react';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';
import { Button, Calendar, Form, InputText, Message, Sidebar, Skeleton } from '@verclam/ui-kit';

const skeletonStackStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	margin: 0.75rem 1.25rem;
`;

const FORM_SKELETON_FIELD_COUNT = 6;
const skeletonFieldIndexes = [...Array(FORM_SKELETON_FIELD_COUNT).keys()];

/**
 * One panel for both creation and edition, told apart by `beneficiaryId` on the signal's context. In
 * edit mode, the dossier is fetched straight from the store (`fetchBeneficiary`) rather than passed in
 * as a prop: the owning pages mount this component once, with no data of their own to pass it.
 */
const UpsertBeneficiarySidebar: React.FC = () => {
	const dispatch = useDispatch();
	const context = useSignalValue(beneficiarySidebarSignal);
	const { currentFacility } = useStrictCurrentFacility();
	const { user: authenticatedUser } = useStrictAuthenticatedUser();
	const canSupervise = useBeneficiaryAccess().access?.canSupervise === true;

	const [formData, setFormData] = React.useState(() => BeneficiaryFormData.createEmpty());
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const formId = React.useId();
	const beneficiaryId = context?.beneficiaryId ?? null;
	const isEditMode = beneficiaryId !== null;

	const hasNameDuplicate = useBeneficiaryNameHomonymCheck(
		currentFacility.id,
		formData.lastName,
		beneficiaryId,
	);

	const { data: fetchedBeneficiary, error } = useStoreQuery({
		selector: (state) => state.beneficiariesStore.beneficiary,
		stopOn: (value) => value !== null && value.id === beneficiaryId,
		query: () => {
			if (beneficiaryId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchBeneficiary(new FetchBeneficiary(currentFacility.id, beneficiaryId)));
		},
		// Switching facilities in the header selector must refetch: `currentFacility.id` stays a
		// dependency even though it can no longer be `null` here.
		deps: [beneficiaryId, currentFacility.id],
	});

	// Guards against the store slot still carrying a previously edited dossier: the panel stays mounted
	// across every "Modifier" click, unlike the page it replaces, which remounted per navigation.
	const editedBeneficiary =
		fetchedBeneficiary !== null && fetchedBeneficiary.id === beneficiaryId ? fetchedBeneficiary : null;
	const isLoadingEditedBeneficiary = isEditMode && editedBeneficiary === null && error === null;

	React.useEffect(() => {
		if (context === null) {
			return;
		}

		if (editedBeneficiary !== null) {
			setFormData(BeneficiaryFormData.fromView(editedBeneficiary));
			return;
		}

		if (!isEditMode) {
			setFormData(BeneficiaryFormData.createEmpty([authenticatedUser.id]));
		}
	}, [context, editedBeneficiary, isEditMode, authenticatedUser]);

	const handleFirstNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setFirstName(value));
	}, []);

	const handleLastNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setLastName(value));
	}, []);

	const handleBirthNameChange = React.useCallback((value: string) => {
		setFormData((current) => current.setBirthName(value));
	}, []);

	const handleBirthDateChange = React.useCallback((value: Date | null) => {
		setFormData((current) => current.setBirthDate(value !== null ? PlainDate.fromCalendar(value) : null));
	}, []);

	const handleHomeAddressChange = React.useCallback((value: string) => {
		setFormData((current) => current.setHomeAddress(value));
	}, []);

	const handlePhoneNumberChange = React.useCallback((value: string) => {
		setFormData((current) => current.setPhoneNumber(value));
	}, []);

	const handleNationalHealthIdentifierChange = React.useCallback((value: string) => {
		setFormData((current) => current.setNationalHealthIdentifier(value));
	}, []);

	const handleAssignedUserIdsChange = React.useCallback((value: readonly number[]) => {
		setFormData((current) => current.setAssignedUserIds(value));
	}, []);

	const handleSubmit = React.useCallback(() => {
		if (!formData.isFormDataValid()) {
			setFormData(formData.showErrors());
			return;
		}

		const facilityId = currentFacility.id;
		const birthName = formData.birthName === '' ? null : formData.birthName;
		const homeAddress = formData.homeAddress === '' ? null : formData.homeAddress;
		const phoneNumber = formData.phoneNumber === '' ? null : formData.phoneNumber;
		const nationalHealthIdentifier =
			formData.nationalHealthIdentifier === '' ? null : formData.nationalHealthIdentifier;

		setIsSubmitting(true);

		if (beneficiaryId === null) {
			dispatch(
				createBeneficiary(
					new CreateBeneficiary(
						facilityId,
						formData.firstName,
						formData.lastName,
						birthName,
						formData.birthDate,
						homeAddress,
						phoneNumber,
						nationalHealthIdentifier,
						formData.assignedUserIds,
					),
				),
			)
				.then(() => closeBeneficiarySidebar())
				.catch((action: BeneficiaryCreationFailedAction) =>
					updateValidationFromExternalSource({
						action,
						eventNames: 'BENEFICIARY_CREATION_FAILED',
						formDataUpdater: setFormData,
					}),
				)
				.finally(() => setIsSubmitting(false));
			return;
		}

		dispatch(
			updateBeneficiary(
				new UpdateBeneficiary(
					facilityId,
					beneficiaryId,
					formData.firstName,
					formData.lastName,
					birthName,
					formData.birthDate,
					homeAddress,
					phoneNumber,
					nationalHealthIdentifier,
					formData.assignedUserIds,
				),
			),
		)
			.then(() => closeBeneficiarySidebar())
			.catch((action: BeneficiaryUpdateFailedAction) =>
				updateValidationFromExternalSource({
					action,
					eventNames: 'BENEFICIARY_UPDATE_FAILED',
					formDataUpdater: setFormData,
				}),
			)
			.finally(() => setIsSubmitting(false));
	}, [formData, currentFacility, beneficiaryId, dispatch]);

	const panelHeader = isEditMode ? 'Modifier le dossier bénéficiaire' : 'Nouveau dossier bénéficiaire';
	const submitLabel = isEditMode ? 'Enregistrer les modifications' : 'Enregistrer';
	const formAriaLabel = isEditMode ? 'Modifier un dossier bénéficiaire' : 'Créer un dossier bénéficiaire';

	const renderFormBody = (): React.ReactNode => {
		if (error !== null) {
			return (
				<Message
					severity="error"
					text="Impossible de charger le dossier. Fermez ce panneau puis réessayez."
				/>
			);
		}

		if (isLoadingEditedBeneficiary) {
			return (
				<div css={skeletonStackStyle}>
					{skeletonFieldIndexes.map((index) => (
						<Skeleton key={index} height="3rem" animation="wave" shape="rectangle" />
					))}
				</div>
			);
		}

		return (
			<React.Fragment>
				{!isEditMode && (
					<Message
						severity="info"
						text={`Rattaché à l’agence ${currentFacility.name} — un dossier appartient à une agence et une seule.`}
					/>
				)}
				<FormSection title="Identité">
					<InputText
						name="nationalHealthIdentifier"
						label="INS"
						subLabel="15 caractères, clé de contrôle comprise"
						value={formData.nationalHealthIdentifier ?? ''}
						onChange={handleNationalHealthIdentifierChange}
						errorMessage={formData.getValidationMessage('nationalHealthIdentifier')}
						isFormFieldValid={formData.isValid('nationalHealthIdentifier')}
						autoFocus
					/>
					<InputText
						name="firstName"
						label="Prénom"
						required
						value={formData.firstName}
						onChange={handleFirstNameChange}
						errorMessage={formData.getValidationMessage('firstName')}
						isFormFieldValid={formData.isValid('firstName')}
					/>
					<InputText
						name="lastName"
						label="Nom"
						required
						value={formData.lastName}
						onChange={handleLastNameChange}
						errorMessage={formData.getValidationMessage('lastName')}
						isFormFieldValid={formData.isValid('lastName')}
					/>
					{hasNameDuplicate ? (
						<Message
							severity="warn"
							text="Un dossier au même nom existe déjà dans cette agence. L’enregistrement reste possible : c’est à vous d’en juger."
						/>
					) : null}
					<Calendar
						name="birthDate"
						label="Date de naissance"
						dateFormat="dd/mm/yy"
						maxDate={new Date()}
						value={formData.birthDate !== null ? formData.birthDate.toCalendar() : null}
						onChange={handleBirthDateChange}
						errorMessage={formData.getValidationMessage('birthDate')}
						isFormFieldValid={formData.isValid('birthDate')}
					/>
					<InputText
						name="birthName"
						label="Nom de naissance"
						value={formData.birthName ?? ''}
						onChange={handleBirthNameChange}
						errorMessage={formData.getValidationMessage('birthName')}
						isFormFieldValid={formData.isValid('birthName')}
					/>
				</FormSection>
				<FormSection title="Coordonnées">
					<InputText
						name="homeAddress"
						label="Adresse du domicile"
						value={formData.homeAddress ?? ''}
						onChange={handleHomeAddressChange}
						errorMessage={formData.getValidationMessage('homeAddress')}
						isFormFieldValid={formData.isValid('homeAddress')}
					/>
					<InputText
						name="phoneNumber"
						label="Téléphone"
						type="tel"
						value={formData.phoneNumber ?? ''}
						onChange={handlePhoneNumberChange}
						errorMessage={formData.getValidationMessage('phoneNumber')}
						isFormFieldValid={formData.isValid('phoneNumber')}
					/>
				</FormSection>
				<ConditionalLoader data={canSupervise}>
					<FormSection title="Affectation">
						<AssignedUsersField
							value={formData.assignedUserIds}
							onChange={handleAssignedUserIdsChange}
						/>
					</FormSection>
				</ConditionalLoader>
			</React.Fragment>
		);
	};

	return (
		<Sidebar
			visible={context !== null}
			onHide={closeBeneficiarySidebar}
			position="right"
			header={panelHeader}
			footer={
				<React.Fragment>
					<Button
						type="button"
						severity="secondary"
						outlined
						label="Annuler"
						onClick={closeBeneficiarySidebar}
					/>
					<Button
						type="submit"
						form={formId}
						label={submitLabel}
						loading={isSubmitting}
						disabled={isLoadingEditedBeneficiary || error !== null}
					/>
				</React.Fragment>
			}
			blockScroll
		>
			<Form id={formId} onSubmit={handleSubmit} aria-label={formAriaLabel}>
				{renderFormBody()}
			</Form>
		</Sidebar>
	);
};

export default UpsertBeneficiarySidebar;
